import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Dispatch, Image, Range, State } from "../types";
import { Background as BackgroundComponent } from "./Background";
import { Blocks } from "./Blocks";
import { InputHandler } from "./InputHandler";
import { useResizableElement } from "./useResizableElement";
import { useScrollToActivePageBlock } from "./useScrollToActivePageBlock";

type Props = Readonly<{
  dispatch: Dispatch;
  gutterWidth: number;
  images: ReadonlyArray<Image>;
  state: State;
}>;

const Background = React.memo(BackgroundComponent);

export const PageView = ({ dispatch, gutterWidth, images, state }: Props) => {
  const [ref, setRef, height, width] = useResizableElement<HTMLDivElement>();
  const [readyPages, setReadyPages] = useState<Array<boolean>>([]);
  const [imagesOnCurrentPage, setImagesOnCurrentPage] = useState<Array<Image>>([]);
  const [previousCurrentPage, setPreviousCurrentPage] = useState<number>(0);
  const [pageOffsets, setPageOffsets] = useState<Array<Range>>([]);
  const [scale, setScale] = useState<number>(0);
  const [canvasHeight, setCanvasHeight] =  useState<number>(0);

  useEffect(() => {
    updateCanvasState();
  }, [images, state.currentPage, state.pageSize]);
 
  useEffect(() => {
    if (state.currentPage > previousCurrentPage) {
      ref?.scroll(0, 0);
    } else {
      ref?.scroll(0, 10000000); // No need to get the exact height, just make sure we go beyond it
    }
    setPreviousCurrentPage(state.currentPage)
  }, [state.currentPage]);
 
 
  const updateCanvasState = () => {
    let imagesOnPage = images.slice(state.currentPage * state.pageSize, (state.currentPage + 1) * state.pageSize)

    const maxPageWidth = Math.max(...imagesOnPage.map(i => i.width));
    let currentScale = (width - gutterWidth * 2) / maxPageWidth
    const offsets: Array<Range> = [];
    let offset = gutterWidth;
    for (const image of imagesOnPage) {
      offsets.push({
        end: offset + image.height * currentScale,
        start: offset,
      });
      offset += image.height * currentScale + gutterWidth;
    }

    let currentCanvasHeight = gutterWidth;
    for (let i = 0; i < imagesOnPage.length; i++) {
      currentCanvasHeight += (imagesOnPage[i].height * currentScale) + gutterWidth;
    }

    setCanvasHeight(currentCanvasHeight);
    setImagesOnCurrentPage(imagesOnPage);
    setPageOffsets(offsets);
    setScale(currentScale);
  }

  const markPageReady = useCallback(
    (page: number) => {
      setReadyPages(p => {
        if (p[page]) {
          return p;
        }
        return [...p.slice(0, page), true, ...p.slice(page + 1)];
      });
    },
    [setReadyPages],
  );

  // https://stackoverflow.com/questions/6081483/maximum-size-of-a-canvas-element
  // We know the max size of a canvas and we know how big the pages are going to be
  // once rendered, so we can calculate a page size that is sure to fit.
  useEffect(() => {
    // only execute this code block once after both of these have been set
    // otherwise there is a race condition.  
    if (width == 0 || images.length == 0) {
      return;
    }
    // Get an upper bound for the biggest page possible
    // Since aspect ratio is maintained, the narrowest page will be the longest
    // This will change whenever the user changes the width of the screen 
    // (narrower screen makes the images less long and thus allows to view more within our limit)
    const maxPageWidth = Math.min(...images.map(i => i.width));
    const minScale = (width - gutterWidth * 2) / maxPageWidth;
 
    let sortedImages = [...images].sort((x1, x2) => x2.height - x1.height);
    let totalHeight = gutterWidth;
    let pageSize = 0;
    for (let i = 0; i < sortedImages.length; i++) {
      totalHeight += (sortedImages[i].height * minScale) + gutterWidth;
      if (totalHeight > 32000) {
        pageSize = i;
        break;
      }
    }
    // If all images fit in one page, we must trigger
    // a redraw manually as it will not go through the page size change loop
    if (pageSize > 0) {
      dispatch({ 
        pageSize: pageSize != 0 ? pageSize : sortedImages.length,
        numImages: images.length,
        type: "SET PAGE SIZE",
      });  
    } else {
      updateCanvasState();
    }
  }, [width, images]);

  useScrollToActivePageBlock(
    ref,
    state.scrollToPageBlock ? state.activeBlocks[state.scrollToPageBlock] : undefined,
    scale,
    pageOffsets,
  );
  return (
    <div className="page" ref={setRef}>
      {ref && height && width && (
        <>
          <Background
            gutterWidth={gutterWidth}
            height={canvasHeight}
            images={imagesOnCurrentPage}
            markPageReady={markPageReady}
            pageRanges={pageOffsets}
            readyPages={readyPages}
            scale={scale}
            width={width}
          />
          <Blocks
            gutterWidth={gutterWidth}
            height={canvasHeight}
            pageRanges={pageOffsets}
            readyPages={readyPages}
            scale={scale}
            showReadingOrder={state.showReadingOrder}
            state={state}
            width={width}
          />
          <InputHandler
            dispatch={dispatch}
            gutterWidth={gutterWidth}
            height={canvasHeight}
            pageRanges={pageOffsets}
            scale={scale}
          /> 
        </>
      )}
    </div>
  );
};
